import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FC, MouseEvent, useEffect, useState } from 'react';
import {
  useController,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import { FormLayout, Larger } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import {
  ChannelResource,
  FBImageDistributionResource,
} from '@/types/resources';
import { api } from '@/utils/api';
import { ImageDistributionStatus } from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';
import { RootState } from '@/utils/store';

import FirstCommentBox from './FirstCommentBox';
import VariationBox, {
  CheckboxHolder,
  Checkmark,
  Input,
  StyledLabel,
} from './VariationBox';

export interface FormDataT {
  name: string;
  link: string;
  image: string;
  owner: string;
  message: string;
  note: string;
  comment: string;
  status: ImageDistributionStatus;
  channel_id: number | null;
  post_date: string;
  is_instant: boolean;
  variations: {
    channel_id: number | null;
    post_date: string;
    is_instant: boolean;
  }[];
}

const AddButtonHolder = styled.div`
  text-align: right;
  margin-bottom: 8px;
  svg {
    margin-right: 8px;
  }
  button {
    width: 100%;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.grayscale};
  margin: 8px 0;
`;

const StyledButton = styled(Button)`
  padding: 0 6px;
`;

export const CommentHolder = styled.div`
  position: relative;
`;

export const CommentNote = styled(Text)`
  font-weight: 500;
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 12px;
  strong {
    font-weight: 700;
  }
`;

interface Props {
  onClose: () => void;
  post?: FBImageDistributionResource;
}

const PostModal: FC<Props> = ({ onClose, post }) => {
  const [hasFirstComment, setHasFirstComment] = useState(
    !!post?.comment || false,
  );

  const navigate = useNavigate();

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const userName = useSelector((state: RootState) => state.auth.user!.name);

  const { data: channels } = useQuery(
    ['channels', { onlyBp: true, authorized: true }],
    () =>
      api.get<never, ChannelResource[]>(`/v1/channels/authorized`, {
        params: {
          only_bp: 1,
        },
      }),
    {
      staleTime: 1000 * 60,
      select: (data) =>
        data.map((channel) => ({ label: channel.name, value: channel.id })),
    },
  );

  const {
    handleSubmit,
    clearErrors,
    control,
    setError,
    setValue,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      name: post?.name ?? '',
      message: post?.message ?? '',
      link: post?.link ?? '',
      owner: post?.owner ?? userName,
      comment: post?.comment,
      image: '',
      status: post?.status ?? ImageDistributionStatus.DRAFT,
      post_date: post?.postDate ?? '',
      is_instant: !post?.postDate,
      channel_id: post?.channel?.id ?? null,
      variations: [{ channel_id: null, post_date: '', is_instant: false }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'variations',
  });

  const { field } = useController<FormDataT, `is_instant`>({
    name: `is_instant`,
    control,
  });

  const status = useWatch<FormDataT, 'status'>({
    name: 'status',
    control,
  });

  const onSubmit = (
    e: MouseEvent<HTMLButtonElement>,
    status: ImageDistributionStatus,
  ) => {
    clearErrors();
    setValue('status', status);
    return handleSubmit(async (data) => {
      try {
        const body = {
          ...data,
          comment: hasFirstComment ? data.comment : null,
        };

        if (post) {
          await api.put<FormDataT, { id: number }>(
            `/v1/distribution/image-posts/${post.id}`,
            body,
          );

          await queryClient.invalidateQueries(['fb_image_distribution'], {
            exact: false,
          });

          alerts.success('A post has been edited successfully');

          return onClose();
        }

        await api.post(`/v1/distribution/image-posts`, body);

        await queryClient.invalidateQueries(['fb_image_distribution'], {
          exact: false,
        });

        alerts.success('A new post has been created successfully');

        onClose();

        navigate(`/general/image-distribution`);
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
      }
    })(e);
  };

  useEffect(() => {
    if (field.value) {
      setValue(`post_date`, '');
    }
  }, [field.value, setValue]);

  return (
    <Modal>
      <SidePanel
        heading={post ? 'Edit schedule' : 'New schedule'}
        onClose={onClose}
      >
        <SidePanelBody autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField
                label="Post Name"
                control={control}
                name="name"
                placeholder="Add name"
              />
              <FormField<FormDataT>
                label="Image"
                control={control}
                name="image"
                type="image"
                id="image"
                notes="JPG, PNG. Aspect ratio: any"
                existingMedia={post?.image}
              />
              <FormField label="Article Link" control={control} name="link" />
              <FormField label="Owner" control={control} name="owner" />
              <Larger>
                <Divider />
              </Larger>
              <Larger>
                <CommentHolder>
                  <FormField
                    label="Share Message"
                    control={control}
                    name="message"
                    type="textarea"
                  />
                  <CommentNote $color="gray" $type="smallBold">
                    <strong>Note:</strong> If first comment isn't activated,
                    article link will be automatically pasted from Article Link
                    field.
                  </CommentNote>
                </CommentHolder>
              </Larger>
              <Larger>
                <FirstCommentBox
                  hasFirstComment={hasFirstComment}
                  setHasFirstComment={setHasFirstComment}
                />
              </Larger>
              {hasFirstComment && (
                <Larger>
                  <CommentHolder>
                    <FormField
                      label="Comment Message"
                      control={control}
                      name="comment"
                      type="textarea"
                      placeholder="Add comment"
                    />
                    <CommentNote $color="gray" $type="smallBold">
                      <strong>Note:</strong> Article link will be automatically
                      pasted from Article Link field.
                    </CommentNote>
                  </CommentHolder>
                </Larger>
              )}
              {post ? (
                <>
                  <FormField<FormDataT, number>
                    name={`channel_id`}
                    control={control}
                    label="Facebook Page"
                    type="select"
                    options={channels || []}
                  />
                  <FormField<FormDataT>
                    label="Date & Time"
                    control={control}
                    name={`post_date`}
                    type="datepicker"
                    disabled={field.value}
                  />
                  <CheckboxHolder>
                    <StyledLabel>
                      Instant post
                      <Input
                        type="checkbox"
                        checked={field.value}
                        onChange={() => {
                          field.onChange(!field.value);
                        }}
                      />
                      <Checkmark />
                    </StyledLabel>
                  </CheckboxHolder>
                </>
              ) : (
                <>
                  <Larger>
                    {fields.map((item, index) => (
                      <VariationBox
                        key={item.id}
                        index={index}
                        remove={remove}
                        fields={fields}
                        channels={channels}
                        control={control}
                        setValue={setValue}
                      />
                    ))}
                  </Larger>
                  <Larger>
                    <AddButtonHolder>
                      <Button
                        type="button"
                        $type="colorized"
                        onClick={() =>
                          append({
                            channel_id: null,
                            post_date: '',
                            is_instant: false,
                          })
                        }
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8 2V14"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M2 8H14"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        Add additional page & time
                      </Button>
                    </AddButtonHolder>
                  </Larger>
                </>
              )}

              <FormField
                label="Note (Internal Information)"
                control={control}
                name="note"
                type="textarea"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={
                isSubmitting && status === ImageDistributionStatus.POSTED
              }
              type="submit"
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                onSubmit(e, ImageDistributionStatus.POSTED);
              }}
            >
              Schedule
            </Button>
            <StyledButton
              color="secondary"
              type="submit"
              disabled={isSubmitting}
              loading={isSubmitting && status === ImageDistributionStatus.DRAFT}
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                onSubmit(e, ImageDistributionStatus.DRAFT);
              }}
            >
              Save to Draft
            </StyledButton>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default PostModal;
